import { useState } from "react";
import axios from "axios";

export const ADMIN_PERMISSION_OPTIONS = [
  ["orders.view", "查看订单"],
  ["orders.manage", "新增及修改订单"],
  ["prices.view", "查看价格"],
  ["prices.manage", "设置及修改价格"],
  ["dispatch.manage", "派单及更换司机"],
  ["finance.view", "查看财务"],
  ["finance.manage", "财务操作"],
  ["audit.view", "查看操作记录"],
];


const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join("；");
  }
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail);
  return error?.message || "未知错误";
};

const initial = {
  name: "",
  email: "",
  phone: "",
  password: "",
  permissions: ["orders.view"],
  managed_driver_ids: [],
};

const buildAccountPayload = (form) => ({
  name: form.name,
  email: form.email,
  phone: form.phone || null,
  permissions: form.permissions,
  managed_driver_ids: form.permissions?.includes("dispatch.manage") ? form.managed_driver_ids || [] : [],
});

export default function useAdminDispatchers({ apiBaseUrl }) {
  const [dispatchers, setDispatchers] = useState([]);
  const [dispatchersLoading, setLoading] = useState(false);
  const [showDispatcherForm, setShow] = useState(false);
  const [editingDispatcher, setEditing] = useState(null);
  const [dispatcherForm, setDispatcherForm] = useState(initial);

  const fetchDispatchers = async () => {
    setLoading(true);
    try {
      setDispatchers((await axios.get(`${apiBaseUrl}/admin/accounts?include_deleted=true`)).data || []);
    } catch (e) {
      if (e.response?.status !== 403) console.error(e);
      setDispatchers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDispatcher = async () => {
    if (!dispatcherForm.name || !dispatcherForm.email || dispatcherForm.password.length < 8) {
      return alert("请填写姓名、登录邮箱和至少8位临时密码");
    }
    try {
      await axios.post(`${apiBaseUrl}/admin/accounts`, {
        ...buildAccountPayload(dispatcherForm),
        temporary_password: dispatcherForm.password,
      });
      alert("管理员账号创建成功，首次登录必须修改密码");
      setShow(false);
      setDispatcherForm(initial);
      fetchDispatchers();
    } catch (e) {
      alert("创建失败: " + getErrorMessage(e));
    }
  };

  const handleUpdateDispatcher = async () => {
    if (!editingDispatcher) return;
    try {
      await axios.put(`${apiBaseUrl}/admin/accounts/${editingDispatcher.id}`, buildAccountPayload(dispatcherForm));
      if (dispatcherForm.password) {
        if (dispatcherForm.password.length < 8) return alert("临时密码至少8位");
        await axios.post(`${apiBaseUrl}/admin/accounts/${editingDispatcher.id}/reset-password`, {
          temporary_password: dispatcherForm.password,
        });
      }
      alert("管理员账号已更新");
      setShow(false);
      setEditing(null);
      fetchDispatchers();
    } catch (e) {
      alert("更新失败: " + getErrorMessage(e));
    }
  };

  const handleToggleDispatcherActive = async (id, status) => {
    try {
      const action = status === "deleted" ? "restore" : status === "approved" ? "suspend" : "activate";
      await axios.post(`${apiBaseUrl}/admin/accounts/${id}/${action}`);
      fetchDispatchers();
    } catch (e) {
      alert("操作失败: " + getErrorMessage(e));
    }
  };

  const handleDeleteDispatcher = async (id, name) => {
    if (!window.confirm(`确定删除管理员账号「${name}」吗？\n历史操作记录会保留。`)) return;
    try {
      await axios.delete(`${apiBaseUrl}/admin/accounts/${id}`);
      fetchDispatchers();
    } catch (e) {
      alert("操作失败: " + getErrorMessage(e));
    }
  };

  const openCreateDispatcherForm = () => {
    setEditing(null);
    setDispatcherForm(initial);
    setShow(true);
  };

  const openEditDispatcherForm = (account) => {
    setEditing(account);
    setDispatcherForm({
      name: account.name || "",
      email: account.email || "",
      phone: account.phone || "",
      password: "",
      permissions: account.permissions || [],
      managed_driver_ids: account.managed_driver_ids || [],
    });
    setShow(true);
  };

  const closeDispatcherForm = () => {
    setShow(false);
    setEditing(null);
    setDispatcherForm(initial);
  };

  return {
    closeDispatcherForm,
    dispatcherForm,
    dispatchers,
    dispatchersLoading,
    editingDispatcher,
    fetchDispatchers,
    handleCreateDispatcher,
    handleDeleteDispatcher,
    handleToggleDispatcherActive,
    handleUpdateDispatcher,
    openCreateDispatcherForm,
    openEditDispatcherForm,
    setDispatcherForm,
    showDispatcherForm,
  };
}
